import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  IconButton,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TelegramIcon from '@mui/icons-material/Telegram';
import RedditIcon from '@mui/icons-material/Reddit';

const Footer: React.FC = () => {
  const theme = useTheme();

  const socialLinks = [
    { name: 'Twitter', icon: TwitterIcon, href: '', color: '#1DA1F2' },
    { name: 'LinkedIn', icon: LinkedInIcon, href: 'https://www.linkedin.com/company/knoowl-ai', color: '#0077B5' },
    { name: 'YouTube', icon: YouTubeIcon, href: '', color: '#FF0000' },
    { name: 'Telegram', icon: TelegramIcon, href: '', color: '#0088cc' },
    { name: 'Reddit', icon: RedditIcon, href: '', color: '#FF4500' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}20, transparent)`,
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
            },
            gap: 4,
            alignItems: 'start',
          }}
        >
          {/* Logo and Brand */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 2, mb: 2 }}>
              <Box
                component="img"
                src="/knoowl-logo.png"
                alt="Knoowl Logo"
                sx={{
                  height: 40,
                  width: 'auto',
                  filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                mb: 3,
              }}
            >
              Transforming education through AI-powered personalized learning experiences.
            </Typography>
            
            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${social.color}15 0%, ${social.color}25 100%)`,
                    border: `1px solid ${social.color}30`,
                    color: social.color,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                      background: `linear-gradient(135deg, ${social.color}25 0%, ${social.color}35 100%)`,
                      boxShadow: `0 8px 25px ${social.color}40`,
                    },
                  }}
                >
                  <social.icon sx={{ fontSize: 20 }} />
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* CTA Section */}
          <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'text.primary',
                fontFamily: '"Charter", "Georgia", serif',
              }}
            >
              Ready to Transform Learning?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 3,
                lineHeight: 1.6,
                fontFamily: '"Kievit", "Roboto", sans-serif',
              }}
            >
              Schools and educators? Contact us to pilot Knoowl in your institution.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://www.knoowl.in/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 16px ${theme.palette.primary.main}25`,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}35`,
                },
              }}
            >
              Try Beta
            </Button>
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Kievit", "Roboto", sans-serif',
            }}
          >
            © 2024 Knoowl. All rights reserved. Powered by AI and blockchain technology.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 